'use client';

/**
 * Centralized `<style jsx>` block for the floating-coupon surface.
 * Rendered once by the orchestrator. Marker class names (`.floating-coupon-body`,
 * `.floating-coupon-modal-transparent`, `.floating-coupon-swiper`, `.coupon-name`,
 * `.collect-button`, `.collect-button-label`, `.card-close-icon`) are scoped
 * globally so they apply across the modal/popup and through the swiper.
 */
export const CollectableCouponStyles = () => (
  <style jsx>{`
    :global(.floating-coupon-swiper .swiper-slide) {
      width: auto !important;
    }

    :global(.floating-coupon-swiper .swiper-wrapper) {
      justify-content: flex-start !important;
    }

    /* Only the active slide is visible; non-active slides are hidden
       via visibility (not display:none) so Swiper can still measure
       their width for the navigation arrow state. */
    :global(.floating-coupon-swiper .swiper-slide) {
      visibility: hidden !important;
    }
    :global(.floating-coupon-swiper .swiper-slide.swiper-slide-active) {
      visibility: visible !important;
    }

    /* Hide the modal heading bottom border — the modal sits inside a
       transparent overlay on mobile, so the divider is visually noisy. */
    :global(.modal-heading) {
      border-bottom: none !important;
    }

    /* Modal heading text style (Figtree 24px / Bold / 120% line height). */
    :global(.modal-heading h4) {
      font-family: 'Figtree', sans-serif !important;
      font-weight: 700 !important;
      font-style: Bold !important;
      font-size: 24px !important;
      line-height: 120% !important;
      letter-spacing: 0% !important;
      padding-bottom: 0.1rem !important;
    }

    /* Forcefully drop the dialog box-shadow when the modal is rendered in
       mobile mode so the transparent overlay does not cast a shadow on
       the page. The dialog wrapper carries the
       floating-coupon-modal-transparent class in that case. */
    :global(.floating-coupon-modal-transparent) {
      box-shadow: none !important;
    }

    /* Floating-coupon modal body padding — this file's modal must
       own its own padding, ignoring Modal.tsx's px-8 default. */
    :global(.floating-coupon-body) {
      padding-left: 3rem !important;
      padding-right: 3rem !important;
    }

    /* Card-level close icon (mobile, isOneCardLayout). */
    :global(.card-close-icon) {
      cursor: pointer;
    }

    /* Modal-level close icon (desktop/tablet). */
    :global(.modal-heading > span) {
      border-radius: 100%;
    }
    :global(.modal-heading > span > svg) {
      border: 1.5px solid #000;
      border-radius: 100%;
    }

    /* =========================================================
       Responsive sizing for the coupon card chrome
       (coupon name + Collect button + Collect button label).
       Smaller viewports get smaller text/button so the card
       stays readable; desktop scales up to an 18px cap on the
       name. Breakpoints mirror the project tailwind config:
         default     <540px
         xs          ≥540px
         sm          ≥640px (default tailwind)
         md          ≥768px
       ========================================================= */

    /* Coupon name — base (mobile, <540px). */
    :global(.coupon-name) {
      font-size: 14px !important;
    }
    @media (min-width: 540px) {
      :global(.coupon-name) {
        font-size: 16px !important;
      }
    }
    @media (min-width: 640px) {
      :global(.coupon-name) {
        font-size: 18px !important;
      }
    }
    @media (min-width: 768px) {
      :global(.coupon-name) {
        font-size: 18px !important; /* 18px cap reached on tablet/desktop. */
      }
    }

    /* Collect button — base dimensions (mobile, <540px). */
    :global(.collect-button) {
      width: 70px;
      height: 36px;
      border-radius: 4px;
      padding: 10px 14px;
      gap: 8px;
    }
    @media (min-width: 540px) {
      :global(.collect-button) {
        width: 78px;
        height: 40px;
        padding: 11px 16px;
      }
    }
    @media (min-width: 640px) {
      :global(.collect-button) {
        width: 81px;
        height: 41px;
        padding: 12px 17px;
      }
    }
    @media (min-width: 768px) {
      :global(.collect-button) {
        width: 83px;
        height: 41px;
        padding: 12px 18px;
      }
    }

    /* Collect button label — base font (mobile, <540px). */
    :global(.collect-button-label) {
      font-size: 12px !important;
    }
    @media (min-width: 540px) {
      :global(.collect-button-label) {
        font-size: 13px !important;
      }
    }
    @media (min-width: 640px) {
      :global(.collect-button-label) {
        font-size: 14px !important;
      }
    }
    /* Capped at 14px on the label — the button itself is small and a
       20px label would overflow. The 20px cap applies to the coupon
       name (the bigger text element). Kept here as a guard. */
  `}</style>
);